import React from 'react';
import { RichTextFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { getOptionTileStateClasses } from '@/ui/components/shared/option-tile/option-tile-styles';
import { twJoin, twMerge } from 'tailwind-merge';
import { ColorDetail } from '../types/ColorDetail';
import { PickerVariant } from '../types/picker-variant';
import { PickerSize } from '../types/picker-size';
import { SelectedCheckBadge } from '../selected-check-badge/selected-check-badge';

export type ColorPickerProps = {
  sectionHeading?: RichTextFragment;
  availableColors: ColorDetail[];
  setSelectedColor: (color: ColorDetail) => void;
  selectedColor?: ColorDetail;
  variant?: PickerVariant;
  size?: PickerSize;
};

interface SwatchState {
  isSelected: boolean;
  isClassic: boolean;
  isSmall: boolean;
}

function getTileClassName({ isSelected, isClassic, isSmall }: Readonly<SwatchState>): string {
  if (isClassic) {
    return twMerge(
      isSmall
        ? 'relative h-9 w-9 rounded-lg border-neutral-50 p-1.5'
        : 'relative h-16 w-16 rounded-lg border-neutral-50 p-3',
      // The classic small tile keeps the 1.5 px selection border Figma draws for it; Tailwind
      // has no token for it.
      isSmall && isSelected ? 'border-[1.5px]' : 'border',
      isSelected ? 'border-neutral-100 bg-primary-5' : 'hover:border-neutral-70',
    );
  }

  return twJoin(
    isSmall
      ? 'relative h-9 w-9 rounded-lg p-1.5'
      : 'relative flex h-16 w-16 items-center justify-center rounded-xl',
    // The small tiles only appear inside the chooser's set rows, so they sit on the row's fill.
    getOptionTileStateClasses(isSelected, { isNested: isSmall }),
  );
}

function getSwatchClassName({ isClassic, isSmall }: Readonly<SwatchState>): string {
  return twJoin(
    isClassic || isSmall ? 'h-full w-full' : 'h-8 w-8',
    'rounded-sm border border-neutral-15',
  );
}

function getLabelColorClassName({ isClassic }: Readonly<SwatchState>): string {
  if (isClassic) {
    return 'text-neutral-55';
  }

  // A grey label made customers read the colour as unavailable, so it stays black.
  return 'text-neutral-100';
}

export function ColorPicker({
  sectionHeading,
  availableColors,
  setSelectedColor,
  selectedColor,
  variant = 'default',
  size = 'default',
}: Readonly<ColorPickerProps>) {
  const isClassic = variant === 'classic';
  const isSmall = size === 'small';

  if (!availableColors?.length) {
    return null;
  }

  return (
    <div>
      {sectionHeading && (
        <RichTextRenderer
          className="text-xl"
          blocks={sectionHeading}
          mutedTextClassName={isClassic ? 'text-neutral-55' : undefined}
        />
      )}
      <div className={twJoin('mt-3 flex items-start', isSmall ? 'space-x-2' : 'space-x-3')}>
        {availableColors.map((color) => {
          const isSelected = selectedColor?.name === color.name;
          const swatchState: SwatchState = { isSelected, isClassic, isSmall };
          // The default chooser arm keeps its English label. The classic layouts and the small
          // bundle rows name the swatch by its CMS colour, so the label follows the locale.
          const accessibleName = isClassic || isSmall ? color.name : `Choose ${color.name}`;

          return (
            <div key={color.name} className="flex flex-col items-center">
              <button
                type="button"
                onClick={() => setSelectedColor(color)}
                className={getTileClassName(swatchState)}
                aria-label={accessibleName}
                aria-pressed={isSelected}
              >
                <div
                  className={getSwatchClassName(swatchState)}
                  style={{ backgroundColor: color.hex }}
                />
                {isClassic && isSelected && <SelectedCheckBadge />}
              </button>
              {!isSmall && (
                <span
                  className={twJoin('mt-2 text-sm font-bold', getLabelColorClassName(swatchState))}
                >
                  {color.name}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
